import React, { useState, useCallback, useRef } from 'react';
import { X, Upload, FileText, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import apiService from '../../services/api';
import BaseModal from '../common/BaseModal';

const ImportTransactionsModal = ({ isOpen, onClose, account, onTransactionsImported }) => {
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState([]);
  const [step, setStep] = useState('upload'); // 'upload', 'row-selection', 'processing', 'preview', 'importing', 'complete'
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [rawCsvData, setRawCsvData] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [rowSelectionPattern, setRowSelectionPattern] = useState(null);
  const [applyPatternToAll, setApplyPatternToAll] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [allSelectedFileData, setAllSelectedFileData] = useState([]); // Store selected rows from all files
  const [analysis, setAnalysis] = useState(null);
  const [mappedTransactions, setMappedTransactions] = useState([]);
  const [selectedTransactions, setSelectedTransactions] = useState(new Set());
  const [error, setError] = useState(null);
  const [isProcessingOFX, setIsProcessingOFX] = useState(false);
  const [currentFileName, setCurrentFileName] = useState('');
  const [isProcessingFiles, setIsProcessingFiles] = useState(false);
  const [fileProgresses, setFileProgresses] = useState({}); // {filename: progress}
  const [forceImportDuplicates, setForceImportDuplicates] = useState(false);
  const processingRef = useRef(false); // Additional guard against multiple calls
  const processingTimeoutRef = useRef(null); // Track timeout to prevent duplicates
  const parsingRef = useRef(false); // Guard against multiple parseFileAtIndex calls

  // Reset state when modal opens to ensure fresh start
  React.useEffect(() => {
    if (isOpen) {
      setDragActive(false);
      setFiles([]);
      setStep('upload');
      setCurrentFileIndex(0);
      setRawCsvData([]);
      setSelectedRows(new Set());
      setRowSelectionPattern(null);
      setApplyPatternToAll(false);
      setAllSelectedFileData([]);
      setAnalysis(null);
      setMappedTransactions([]);
      setSelectedTransactions(new Set());
      setError(null);
      setIsProcessingOFX(false);
      setCurrentFileName('');
      setImportResults(null);
      setIsProcessingFiles(false);
      setFileProgresses({});
      setForceImportDuplicates(false);

      // Reset refs
      processingRef.current = false;
      parsingRef.current = false;
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = null;
      }
    }
  }, [isOpen]);

  const handleClose = () => {
    // Reset ALL state when closing to ensure fresh start
    setDragActive(false);
    setFiles([]);
    setStep('upload');
    setCurrentFileIndex(0);
    setRawCsvData([]);
    setSelectedRows(new Set());
    setRowSelectionPattern(null);
    setApplyPatternToAll(false);
    setAllSelectedFileData([]);
    setAnalysis(null);
    setMappedTransactions([]);
    setSelectedTransactions(new Set());
    setError(null);
    setIsProcessingOFX(false);
    setCurrentFileName('');
    setImportResults(null);
    setIsProcessingFiles(false);
    setFileProgresses({});
    setForceImportDuplicates(false);

    // Reset refs
    processingRef.current = false;
    parsingRef.current = false;
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
      processingTimeoutRef.current = null;
    }

    onClose();
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const fileList = Array.from(e.dataTransfer.files);
      handleFilesSelect(fileList);
    }
  }, []);

  const handleFilesSelect = (selectedFiles) => {
    const validTypes = ['.csv', '.txt', '.ofx', '.qfx', '.qif'];
    const maxSize = 10 * 1024 * 1024; // 10MB
    const maxFiles = 5;
    const validFiles = [];
    const errors = [];

    if (selectedFiles.length > maxFiles) {
      errors.push(`Maximum ${maxFiles} files allowed. Selected ${selectedFiles.length} files.`);
      return;
    }

    selectedFiles.forEach(file => {
      const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));

      if (!validTypes.includes(fileExtension)) {
        errors.push(`${file.name}: Invalid file type. Please use CSV, TXT, OFX, QFX, or QIF files.`);
        return;
      }

      if (file.size > maxSize) {
        errors.push(`${file.name}: File size must be less than 10MB.`);
        return;
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      setError(errors.join('\n'));
      return;
    }

    setFiles(validFiles);
    setError(null);
  };

  // Keep the single file handler for backward compatibility

  // Detect the pattern from user's selection (e.g., "skip first 3 rows")
  const detectSelectionPattern = (selectedRows, allRows) => {
    const selectedArray = Array.from(selectedRows).sort((a, b) => a - b);
    const unselectedArray = allRows.map((_, i) => i).filter(i => !selectedRows.has(i));

    // Detect if there's a consistent pattern like "skip first N rows"
    if (unselectedArray.length > 0 && selectedArray.length > 0) {
      const firstSelected = selectedArray[0];

      // Check if all unselected rows are at the beginning
      const allUnselectedAtStart = unselectedArray.every((idx, i) => {
        if (i === 0) return idx === 0; // First unselected should be index 0
        return idx === i; // All unselected should be consecutive from start
      });

      if (allUnselectedAtStart && firstSelected === unselectedArray.length) {
        // Pattern: skip first N rows
        return {
          type: 'skipFirst',
          skipCount: unselectedArray.length,
          description: `Skip first ${unselectedArray.length} row(s)`
        };
      }
    }

    // If no specific pattern, but we have selections, just remember which indices are selected
    if (selectedArray.length > 0) {
      // Check if all rows are selected
      if (unselectedArray.length === 0) {
        return {
          type: 'selectAll',
          description: 'Select all rows'
        };
      }

      // For simplicity, if first N rows are unselected, use that as pattern
      let skipCount = 0;
      for (let i = 0; i < allRows.length; i++) {
        if (!selectedRows.has(i)) {
          skipCount++;
        } else {
          break;
        }
      }

      if (skipCount > 0) {
        return {
          type: 'skipFirst',
          skipCount: skipCount,
          description: `Skip first ${skipCount} row(s)`
        };
      }
    }

    return null;
  };

  // Apply a detected pattern to new data with sanity check
  const applySelectionPattern = (pattern, allRows) => {
    const newSelection = new Set();

    if (!pattern) {
      // No pattern, use smart detection
      allRows.forEach((row, index) => {
        if (row.isLikelyTransaction) {
          newSelection.add(index);
        }
      });
      return newSelection;
    }

    if (pattern.type === 'selectAll') {
      // Select all rows
      for (let i = 0; i < allRows.length; i++) {
        newSelection.add(i);
      }
      return newSelection;
    }

    if (pattern.type === 'skipFirst') {
      // Sanity check: make sure we have enough rows
      if (allRows.length <= pattern.skipCount) {
        // File has fewer rows than the skip count, select all that look like transactions
        allRows.forEach((row, index) => {
          if (row.isLikelyTransaction) {
            newSelection.add(index);
          }
        });
      } else {
        // Apply the pattern: select all rows after the skip count
        for (let i = pattern.skipCount; i < allRows.length; i++) {
          // Additional sanity check: only if it looks like a transaction
          if (allRows[i].isLikelyTransaction || allRows[i].hasDate || allRows[i].hasAmount) {
            newSelection.add(i);
          }
        }
      }
    }

    return newSelection;
  };

  const parseFilesForRowSelection = () => {
    setAllSelectedFileData([]); // Reset collected data
    parseFileAtIndex(0); // Start with first file
  };


  // Save selected rows and move to next file or process all
  const saveSelectedRowsAndContinue = () => {
    // Detect pattern if first file
    let currentPattern = rowSelectionPattern;
    if (currentFileIndex === 0 && applyPatternToAll && files.length > 1) {
      const pattern = detectSelectionPattern(selectedRows, rawCsvData);
      setRowSelectionPattern(pattern);
      currentPattern = pattern; // Use immediately since setState is async
    }

    // Save selected rows for this file
    const selectedRowsData = rawCsvData.filter((_, index) => selectedRows.has(index));
    const fileData = {
      fileIndex: currentFileIndex,
      fileName: files[currentFileIndex].name,
      rows: selectedRowsData.map(row => row.rawData)
    };

    setAllSelectedFileData(prev => {
      const updatedAllSelected = [...prev, fileData];

      // Check if more files to process
      if (currentFileIndex + 1 < files.length) {
        // Move to next file
        const nextIndex = currentFileIndex + 1;
        setCurrentFileIndex(nextIndex);

        // Use the pattern we just detected or the existing one
        if (currentPattern && applyPatternToAll) {
          // Parse next file and apply pattern automatically (skip UI)
          setTimeout(() => {
            processNextFileWithPatternNoUI(nextIndex, currentPattern, updatedAllSelected);
          }, 100);
        } else {
          // Parse next file for manual selection
          setTimeout(() => {
            parseFileAtIndex(nextIndex);
          }, 100);
        }
      } else {
        // All files processed
        if (processingTimeoutRef.current) {
          clearTimeout(processingTimeoutRef.current);
        }
        processingTimeoutRef.current = setTimeout(() => {
          processingTimeoutRef.current = null;
          if (isAllOFXFiles(files)) {
            processOFXFilesDirectly(updatedAllSelected);
          } else {
            processAllSelectedData(updatedAllSelected);
          }
        }, 50);
      }

      return updatedAllSelected;
    });
  };

  // Parse specific file for row selection
  const parseFileAtIndex = async (fileIndex) => {
    if (!files || fileIndex >= files.length) return;

    // Prevent multiple simultaneous parsing
    if (parsingRef.current) {
      return;
    }

    parsingRef.current = true;

    try {
      const file = files[fileIndex];
      const text = await file.text();

      // Check if this is an OFX or QIF file - if so, skip row selection
      const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;
      if (fileExtension === '.ofx' || fileExtension === '.qfx' || fileExtension === '.qif') {
        // For OFX/QIF files, skip row selection and process entire file
        const fileData = {
          fileIndex,
          fileName: file.name,
          rows: [[text]] // Send entire content as single "row"
        };

        setAllSelectedFileData(prev => {
          const updatedAllSelected = [...prev, fileData];

          // Check if more files to process
          if (fileIndex + 1 < files.length) {
            // Move to next file
            setCurrentFileIndex(fileIndex + 1);
            setTimeout(() => {
              parseFileAtIndex(fileIndex + 1);
            }, 100);
          } else {
            // All files processed
            if (processingTimeoutRef.current) {
              clearTimeout(processingTimeoutRef.current);
            }
            processingTimeoutRef.current = setTimeout(() => {
              processingTimeoutRef.current = null;
              if (isAllOFXFiles(files)) {
                processOFXFilesDirectly(updatedAllSelected);
              } else {
                processAllSelectedData(updatedAllSelected);
              }
            }, 50);
          }

          return updatedAllSelected;
        });
        parsingRef.current = false;
        return;
      }

      // For CSV files, continue with row selection logic
      const lines = text.split('\n').filter(line => line.trim());

      const allRows = lines.map((line, index) => {
        const rawData = line.split(',').map(cell => cell.trim().replace(/^["']|["']$/g, ''));

        const hasDate = rawData.some(cell => {
          return /\d{1,2}[/-]\d{1,2}[/-]\d{2,4}|\d{4}[/-]\d{1,2}[/-]\d{1,2}/.test(cell);
        });

        const hasAmount = rawData.some(cell => {
          return /^\d{1,3}(,\d{3})*(\.\d{2})?$|^\d+(\.\d{2})?$/.test(cell.replace(/^\$/, ''));
        });

        const isLikelyTransaction = hasDate && hasAmount;

        return {
          fileIndex,
          fileName: file.name,
          rowIndex: index,
          rawData,
          isLikelyTransaction,
          hasDate,
          hasAmount
        };
      });

      setRawCsvData(allRows);

      // Apply pattern or use smart detection
      let autoSelected;
      if (rowSelectionPattern && applyPatternToAll) {
        autoSelected = applySelectionPattern(rowSelectionPattern, allRows);
      } else {
        autoSelected = new Set();
        allRows.forEach((row, index) => {
          if (row.isLikelyTransaction) {
            autoSelected.add(index);
          }
        });
      }

      setSelectedRows(autoSelected);
      setStep('row-selection');
      parsingRef.current = false;

    } catch (err) {
      console.error('Error parsing file:', err);
      setError('Failed to parse file.');
      parsingRef.current = false;
    }
  };

  // Process next file with pattern automatically (skip UI)
  const processNextFileWithPatternNoUI = async (fileIndex, patternToUse, currentAllSelected) => {
    if (!files || fileIndex >= files.length) return;

    try {
      const file = files[fileIndex];
      const text = await file.text();

      // Check if this is an OFX or QIF file - if so, skip pattern application
      const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;
      if (fileExtension === '.ofx' || fileExtension === '.qfx' || fileExtension === '.qif') {
        // For OFX/QIF files, process entire file
        const fileData = {
          fileIndex,
          fileName: file.name,
          rows: [[text]] // Send entire content as single "row"
        };

        const updatedAllSelected = [...currentAllSelected, fileData];

        // Check if more files to process
        if (fileIndex + 1 < files.length) {
          setTimeout(() => {
            processNextFileWithPatternNoUI(fileIndex + 1, patternToUse, updatedAllSelected);
          }, 100);
        } else {
          // All files processed
          if (processingTimeoutRef.current) {
            clearTimeout(processingTimeoutRef.current);
          }
          processingTimeoutRef.current = setTimeout(() => {
            processingTimeoutRef.current = null;
            if (isAllOFXFiles(files)) {
              // For OFX files, process directly
              processOFXFilesDirectly(updatedAllSelected);
            } else {
              // For CSV files, process data
              processAllSelectedData(updatedAllSelected);
            }
          }, 50);
        }
        return;
      }

      const lines = text.split('\n').filter(line => line.trim());

      const allRows = lines.map((line, index) => {
        const rawData = line.split(',').map(cell => cell.trim().replace(/^["']|["']$/g, ''));

        const hasDate = rawData.some(cell => {
          return /\d{1,2}[/-]\d{1,2}[/-]\d{2,4}|\d{4}[/-]\d{1,2}[/-]\d{1,2}/.test(cell);
        });

        const hasAmount = rawData.some(cell => {
          return /^\d{1,3}(,\d{3})*(\.\d{2})?$|^\d+(\.\d{2})?$/.test(cell.replace(/^\$/, ''));
        });

        return {
          rawData,
          isLikelyTransaction: hasDate && hasAmount,
          hasDate,
          hasAmount
        };
      });

      // Apply pattern
      const selectedIndices = applySelectionPattern(patternToUse, allRows);
      const selectedRowsData = allRows.filter((_, index) => selectedIndices.has(index));

      // Save selected rows
      const fileData = {
        fileIndex,
        fileName: file.name,
        rows: selectedRowsData.map(row => row.rawData)
      };

      const updatedAllSelected = [...currentAllSelected, fileData];
      setAllSelectedFileData(updatedAllSelected);

      // Check if more files
      if (fileIndex + 1 < files.length) {
        setCurrentFileIndex(fileIndex + 1);
        // Recursively process next file
        setTimeout(() => {
          processNextFileWithPatternNoUI(fileIndex + 1, patternToUse, updatedAllSelected);
        }, 100);
      } else {
        // All files done, check if we can skip AI processing
        if (processingTimeoutRef.current) {
          clearTimeout(processingTimeoutRef.current);
        }
        processingTimeoutRef.current = setTimeout(() => {
          processingTimeoutRef.current = null;
          if (isAllOFXFiles(files)) {
            processOFXFilesDirectly(updatedAllSelected);
          } else {
            processAllSelectedData(updatedAllSelected);
          }
        }, 50);
      }

    } catch (err) {
      console.error('Error processing file with pattern:', err);
      setError('Failed to process file.');
    }
  };

  // Check if all files are OFX/QIF files
  const isAllOFXFiles = (fileList) => {
    return Array.from(fileList).every(file => {
      const extension = `.${file.name.split('.').pop().toLowerCase()}`;
      return extension === '.ofx' || extension === '.qfx' || extension === '.qif';
    });
  };

  // Process OFX files directly without AI analysis
  const processOFXFilesDirectly = async (allFileData) => {
    if (!account || !account.id) {
      setError('Account information not available.');
      return;
    }

    // Prevent multiple concurrent processing with double guard
    if (isProcessingFiles || processingRef.current) {
      return;
    }

    processingRef.current = true;


    setStep('processing');
    setError(null);
    setIsProcessingOFX(true);
    setIsProcessingFiles(true);
    // Use the actual number of unique files instead of potentially duplicated allFileData

    // Initialize all file progresses to 0 - use unique filenames only
    const uniqueFiles = new Map();
    allFileData.forEach(fileData => {
      uniqueFiles.set(fileData.fileName, fileData);
    });
    const uniqueFileData = Array.from(uniqueFiles.values());


    const initialProgresses = {};
    uniqueFileData.forEach(fileData => {
      initialProgresses[fileData.fileName] = 0;
    });
    setFileProgresses(initialProgresses);

    try {
      const allTransactions = [];

      // Process each unique OFX file
      for (let i = 0; i < uniqueFileData.length; i++) {
        const fileData = uniqueFileData[i];
        setCurrentFileName(fileData.fileName);

        // OFX content is stored as [[raw_content]]
        const fileContent = fileData.rows[0][0];
        const blob = new Blob([fileContent], { type: 'application/x-ofx' });
        const file = new File([blob], fileData.fileName, { type: 'application/x-ofx' });

        // Upload and process
        const formData = new FormData();
        formData.append('file', file);
        formData.append('account_id', account.id);

        const response = await apiService.analyzeTransactionFile(formData, (uploadProgress) => {
          const newProgress = uploadProgress * 0.3;
          setFileProgresses(prev => {
            const updated = {
              ...prev,
              [fileData.fileName]: newProgress
            };
            // Overall progress tracked via individual file progress bars
            return updated;
          });
        });

        // Custom polling for individual file progress
        const taskResult = await pollTaskStatusForIndividualFile(response.task_id, fileData.fileName);

        if (taskResult && taskResult.transactions) {
          const newTransactions = taskResult.transactions.map(tx => ({
            ...tx,
            sourceFile: fileData.fileName,
            fileIndex: fileData.fileIndex
          }));
          allTransactions.push(...newTransactions);
        }
      }

      // Check for duplicates before showing results
      try {
        const duplicateResponse = await apiService.checkTransactionDuplicates(account.id, allTransactions);
        const transactionsWithDuplicates = duplicateResponse.transactions;

        // Show results with duplicate information
        setAnalysis({ transactions: transactionsWithDuplicates });
        setMappedTransactions(transactionsWithDuplicates);
        const nonDuplicateIndices = transactionsWithDuplicates
          .map((transaction, index) => transaction.is_duplicate ? null : index)
          .filter(index => index !== null);
        setSelectedTransactions(new Set(nonDuplicateIndices));
      } catch (err) {
        console.error('Error checking duplicates:', err);
        // Fallback to showing without duplicate info
        setAnalysis({ transactions: allTransactions });
        setMappedTransactions(allTransactions);
        const allIndices = allTransactions.map((_, index) => index);
        setSelectedTransactions(new Set(allIndices));
      }

      setStep('preview');
      setIsProcessingFiles(false);
      processingRef.current = false;

    } catch (error) {
      console.error('Error processing OFX files:', error);
      setError(`Error processing files: ${error.message || 'Unknown error'}`);
      setStep('upload');
      setIsProcessingOFX(false);
      setIsProcessingFiles(false);
      processingRef.current = false;
    }
  };

  // Process all selected data from all files with AI
  const processAllSelectedData = async (allFileData) => {
    if (!account || !account.id) {
      setError('Account information not available.');
      return;
    }

    // Prevent multiple concurrent processing with double guard
    if (isProcessingFiles || processingRef.current) {
      return;
    }

    processingRef.current = true;
    setStep('processing');
    setError(null);
    setIsProcessingFiles(true);

    try {
      const allTransactions = [];
      const totalFiles = allFileData.length;

      // Process each file's data
      for (let i = 0; i < allFileData.length; i++) {
        const fileData = allFileData[i];
        const progressStart = (i / totalFiles) * 90;
        const progressRange = 90 / totalFiles;

        // Check if this is an OFX/QIF file (stored as single row with raw content)
        const fileExtension = `.${fileData.fileName.split('.').pop().toLowerCase()}`;
        let fileContent, mimeType;

        if (fileExtension === '.ofx' || fileExtension === '.qfx' || fileExtension === '.qif') {
          // For OFX/QIF, the content is stored as [[raw_content]]
          fileContent = fileData.rows[0][0]; // Get the raw OFX/QIF content
          mimeType = (fileExtension === '.ofx' || fileExtension === '.qfx') ? 'application/x-ofx' : 'text/plain';
        } else {
          // For CSV, create CSV from selected rows
          fileContent = fileData.rows.map(row => row.join(',')).join('\n');
          mimeType = 'text/csv';
        }

        const blob = new Blob([fileContent], { type: mimeType });
        const file = new File([blob], fileData.fileName, { type: mimeType });

        // Upload and process
        const formData = new FormData();
        formData.append('file', file);
        formData.append('account_id', account.id);

        const response = await apiService.analyzeTransactionFile(formData, (uploadProgress) => {
        });

        const taskResult = await pollTaskStatus(response.task_id,
          progressStart + (progressRange * 0.3),
          progressRange * 0.7);

        if (taskResult && taskResult.transactions) {
          const newTransactions = taskResult.transactions.map(tx => ({
            ...tx,
            sourceFile: fileData.fileName,
            fileIndex: fileData.fileIndex
          }));
          allTransactions.push(...newTransactions);
        }

      }

      // Check for duplicates before showing results
      try {
        const duplicateResponse = await apiService.checkTransactionDuplicates(account.id, allTransactions);
        const transactionsWithDuplicates = duplicateResponse.transactions;

        // Show results with duplicate information
        setAnalysis({ transactions: transactionsWithDuplicates });
        setMappedTransactions(transactionsWithDuplicates);
        const nonDuplicateIndices = transactionsWithDuplicates
          .map((transaction, index) => transaction.is_duplicate ? null : index)
          .filter(index => index !== null);
        setSelectedTransactions(new Set(nonDuplicateIndices));
      } catch (err) {
        console.error('Error checking duplicates:', err);
        // Fallback to showing without duplicate info
        setAnalysis({ transactions: allTransactions });
        setMappedTransactions(allTransactions);
        const allIndices = allTransactions.map((_, index) => index);
        setSelectedTransactions(new Set(allIndices));
      }

      setStep('preview');
      setIsProcessingFiles(false);
      processingRef.current = false;

    } catch (err) {
      console.error('Error processing files:', err);
      setError(err.message || 'Failed to process files');
      setStep('upload');
      setIsProcessingFiles(false);
      processingRef.current = false;
    }
  };

  const processSelectedRows = saveSelectedRowsAndContinue;

  const processFiles = parseFilesForRowSelection;

  // Keep backward compatibility
  const processFile = processFiles;

  // Simple polling for individual file progress (0-100%)
  const pollTaskStatusForIndividualFile = async (taskId, fileName) => {
    const maxPollingTime = 300000; // 5 minutes
    const pollingInterval = 2000; // 2 seconds
    const startTime = Date.now();

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const status = await apiService.getTaskStatus(taskId);

          if (status.state === 'SUCCESS') {
            // Task completed successfully
            setFileProgresses(prev => {
              const updated = {
                ...prev,
                [fileName]: 100
              };
              // Overall progress tracked via individual file progress bars
              return updated;
            });
            resolve(status.result);
          } else if (status.state === 'FAILURE') {
            // Task failed
            reject(new Error(status.error || 'File processing failed'));
          } else if (status.state === 'PROGRESS') {
            // Task in progress - use 30% to 100% range for processing
            const newProgress = 30 + ((status.progress || 0) / 100) * 70;
            setFileProgresses(prev => {
              const updated = {
                ...prev,
                [fileName]: newProgress
              };
              // Overall progress tracked via individual file progress bars
              return updated;
            });

            // Continue polling
            if (Date.now() - startTime < maxPollingTime) {
              setTimeout(poll, pollingInterval);
            } else {
              reject(new Error('Processing timeout - please try again'));
            }
          } else {
            // Task pending or other state
            setFileProgresses(prev => {
              const updated = {
                ...prev,
                [fileName]: 35
              };
              // Overall progress tracked via individual file progress bars
              return updated;
            });

            // Continue polling
            if (Date.now() - startTime < maxPollingTime) {
              setTimeout(poll, pollingInterval);
            } else {
              reject(new Error('Processing timeout - please try again'));
            }
          }
        } catch (err) {
          console.error('Error polling task status:', err);
          reject(new Error(err.message || 'Failed to get processing status'));
        }
      };

      // Start polling
      setTimeout(poll, pollingInterval);
    });
  };

  const pollTaskStatus = async (taskId, baseProgress = 30, progressRange = 70) => {
    const maxPollingTime = 300000; // 5 minutes
    const pollingInterval = 2000; // 2 seconds
    const startTime = Date.now();

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const status = await apiService.getTaskStatus(taskId);

          if (status.state === 'SUCCESS') {
            // Task completed successfully
            resolve(status.result);
          } else if (status.state === 'FAILURE') {
            // Task failed
            reject(new Error(status.error || 'File processing failed'));
          } else if (status.state === 'PROGRESS') {
            // Task in progress - scale the progress within our range

            // Continue polling
            if (Date.now() - startTime < maxPollingTime) {
              setTimeout(poll, pollingInterval);
            } else {
              reject(new Error('Processing timeout - please try again'));
            }
          } else {
            // Task pending or other state

            // Continue polling
            if (Date.now() - startTime < maxPollingTime) {
              setTimeout(poll, pollingInterval);
            } else {
              reject(new Error('Processing timeout - please try again'));
            }
          }
        } catch (err) {
          console.error('Error polling task status:', err);
          reject(new Error(err.message || 'Failed to get processing status'));
        }
      };

      // Start polling
      setTimeout(poll, pollingInterval);
    });
  };

  const [importResults, setImportResults] = useState(null);

  const importTransactions = async () => {
    setStep('importing');
    setError(null);

    try {
      const transactionsToImport = mappedTransactions.filter((_, index) =>
        selectedTransactions.has(index)
      );

      const response = await apiService.importTransactions(account.id, {
        transactions: transactionsToImport,
        import_job_id: analysis.import_job_id,
        force_import: forceImportDuplicates
      });

      setImportResults(response);
      setStep('complete');
      onTransactionsImported?.(response);

    } catch (err) {
      console.error('Error importing transactions:', err);
      setError(err.message || 'Failed to import transactions');
      setStep('preview');
    }
  };

  const toggleTransactionSelection = (index) => {
    const newSelection = new Set(selectedTransactions);
    if (newSelection.has(index)) {
      newSelection.delete(index);
    } else {
      newSelection.add(index);
    }
    setSelectedTransactions(newSelection);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: account?.currency || 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    try {
      // Parse date components directly to avoid timezone issues
      const [year, month, day] = dateString.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return date.toLocaleDateString('en-US');
    } catch {
      return dateString;
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={isProcessingFiles || isProcessingOFX || step === 'importing' ? () => {} : handleClose}
      size="xl"
      showCloseButton={false}
      closeOnBackdrop={!isProcessingFiles && !isProcessingOFX && step !== 'importing'}
    >
      <div className="flex items-center justify-between p-6 border-b">
        <h2 className="text-xl font-semibold text-gray-900">
          Import Transactions - {account?.name}
        </h2>
        <button
          type="button"
          onClick={handleClose}
          className="text-gray-400 hover:text-gray-600"
          aria-label="Close modal"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

        <div className="p-6">
          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                step === 'upload' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white'
              }`}>
                <span className="text-sm font-medium">1</span>
              </div>
              <div className={`h-1 w-16 mx-2 ${
                ['processing', 'preview', 'importing', 'complete'].includes(step) ? 'bg-green-600' : 'bg-gray-300'
              }`} />
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                step === 'processing' ? 'bg-blue-600 text-white' :
                ['preview', 'importing', 'complete'].includes(step) ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-600'
              }`}>
                <span className="text-sm font-medium">2</span>
              </div>
              <div className={`h-1 w-16 mx-2 ${
                ['preview', 'importing', 'complete'].includes(step) ? 'bg-green-600' : 'bg-gray-300'
              }`} />
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                step === 'preview' ? 'bg-blue-600 text-white' :
                ['importing', 'complete'].includes(step) ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-600'
              }`}>
                <span className="text-sm font-medium">3</span>
              </div>
            </div>
          </div>

          {/* Step Content */}
          {step === 'upload' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Upload Transaction File</h3>
                <p className="text-gray-600">Upload a CSV, OFX, QIF or TXT file from your bank or financial institution</p>
              </div>

              {/* File Upload Area */}
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive
                    ? 'border-blue-500 bg-blue-50'
                    : files.length > 0
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                {files.length > 0 ? (
                  <div className="space-y-4">
                    <FileText className="h-12 w-12 text-green-600 mx-auto" />
                    <div className="space-y-2">
                      {files.length === 1 ? (
                        <>
                          <p className="text-lg font-medium text-gray-900">{files[0].name}</p>
                          <p className="text-gray-500">{(files[0].size / 1024).toFixed(1)} KB</p>
                        </>
                      ) : (
                        <>
                          <p className="text-lg font-medium text-gray-900">{files.length} files selected</p>
                          <div className="text-sm text-gray-600 space-y-1 max-h-32 overflow-y-auto">
                            {files.map((file, index) => (
                              <div key={index} className="flex justify-between">
                                <span className="truncate">{file.name}</span>
                                <span className="ml-2 text-gray-500">{(file.size / 1024).toFixed(1)} KB</span>
                              </div>
                            ))}
                          </div>
                          <p className="text-gray-500">
                            Total: {(files.reduce((total, file) => total + file.size, 0) / 1024).toFixed(1)} KB
                          </p>
                        </>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setFiles([])}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      Remove {files.length === 1 ? 'file' : 'files'}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Upload className="h-12 w-12 text-gray-400 mx-auto" />
                    <div>
                      <p className="text-lg font-medium text-gray-900">
                        Drop your files here, or{' '}
                        <label className="text-blue-600 hover:text-blue-700 cursor-pointer">
                          browse
                          <input
                            type="file"
                            className="hidden"
                            accept=".csv,.txt,.ofx,.qfx,.qif"
                            multiple
                            onChange={(e) => e.target.files && e.target.files.length > 0 && handleFilesSelect(Array.from(e.target.files))}
                          />
                        </label>
                      </p>
                      <p className="text-gray-500">Supports CSV, OFX, QFX, QIF and TXT files up to 10MB each (max 5 files)</p>
                    </div>
                  </div>
                )}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                    <span className="text-red-700">{error}</span>
                  </div>
                </div>
              )}

              {/* Sample Format Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <h4 className="font-medium text-blue-900 mb-2">Expected Format:</h4>
                <p className="text-blue-800 text-sm">
                  Your file should contain columns for Date, Description, and Amount.
                  Our AI will automatically detect and map these columns. OFX/QFX files are processed automatically.
                </p>
              </div>
            </div>
          )}

          {step === 'row-selection' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Select Transaction Rows ({currentFileIndex + 1} of {files.length})
                </h3>
                <p className="text-gray-600">
                  File: <span className="font-medium">{files[currentFileIndex]?.name}</span>
                </p>
                <p className="text-gray-600">
                  Review the data and select only the rows that contain actual transactions.
                  Deselect header rows and any non-transaction data.
                </p>
              </div>

              <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                <div className="bg-gray-50 px-4 py-2 border-b sticky top-0 space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedRows.size === rawCsvData.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRows(new Set(rawCsvData.map((_, i) => i)));
                        } else {
                          setSelectedRows(new Set());
                        }
                      }}
                      className="rounded border-gray-300 text-blue-600 mr-3"
                    />
                    <span className="font-medium">Select All ({selectedRows.size} of {rawCsvData.length} rows)</span>
                  </label>

                  {/* Show Apply to All checkbox only on first file with multiple files */}
                  {currentFileIndex === 0 && files.length > 1 && (
                    <label className="flex items-center ml-7 text-sm">
                      <input
                        type="checkbox"
                        checked={applyPatternToAll}
                        onChange={(e) => setApplyPatternToAll(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 mr-2"
                      />
                      <span className="text-gray-600">
                        Apply this selection pattern to all {files.length} files
                      </span>
                    </label>
                  )}

                  {/* Show pattern being applied */}
                  {currentFileIndex > 0 && rowSelectionPattern && applyPatternToAll && (
                    <div className="ml-7 text-sm text-blue-600">
                      Pattern applied: {rowSelectionPattern.description}
                    </div>
                  )}
                </div>

                <div className="divide-y divide-gray-200">
                  {rawCsvData.map((row, index) => (
                    <div key={index} className={`px-4 py-2 hover:bg-gray-50 ${
                      row.rowIndex === 0 ? 'bg-yellow-50' : ''
                    }`}>
                      <label className="flex items-start">
                        <input
                          type="checkbox"
                          checked={selectedRows.has(index)}
                          onChange={() => {
                            const newSelected = new Set(selectedRows);
                            if (newSelected.has(index)) {
                              newSelected.delete(index);
                            } else {
                              newSelected.add(index);
                            }
                            setSelectedRows(newSelected);
                          }}
                          className="rounded border-gray-300 text-blue-600 mr-3 mt-1"
                        />
                        <div className="flex-1">
                          <div className="text-xs text-gray-500 mb-1">
                            {row.fileName} - Row {row.rowIndex + 1}
                            {!row.isLikelyTransaction && (
                              <span className="ml-2 text-orange-600">
                                (Header: {!row.hasDate && 'No Date'} {!row.hasAmount && 'No Amount'})
                              </span>
                            )}
                            {row.isLikelyTransaction && (
                              <span className="ml-2 text-green-600">(Transaction ✓)</span>
                            )}
                          </div>
                          <div className="font-mono text-sm text-gray-900 break-all">
                            {row.rawData.join(' | ')}
                          </div>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 'processing' && (
            <div className="space-y-6">
              <div className="text-center">
                <Loader className="h-16 w-16 text-blue-600 mx-auto animate-spin" />
                <div className="mt-4">
                  {isProcessingOFX ? (
                    <>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Processing OFX Files</h3>
                      <p className="text-gray-600">Parsing structured transaction data...</p>
                    </>
                  ) : (
                    <>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Processing with AI</h3>
                      <p className="text-gray-600">Analyzing selected rows and mapping transactions...</p>
                    </>
                  )}
                  {/* Overall progress is tracked via individual fileProgresses, not separate progress state */}
                </div>
              </div>

              {/* Individual File Progress Bars */}
              <div className="space-y-4">
                {files.map((file, index) => {
                  const progress = fileProgresses[file.name] || 0;
                  const isActive = currentFileName === file.name;
                  const isCompleted = progress === 100;

                  return (
                    <div key={index} className={`p-4 border rounded-lg ${
                      isActive ? 'bg-blue-50 border-blue-200' :
                      isCompleted ? 'bg-green-50 border-green-200' :
                      'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className={`text-sm font-medium ${
                            isActive ? 'text-blue-900' :
                            isCompleted ? 'text-green-900' :
                            'text-gray-700'
                          }`}>
                            {file.name}
                          </span>
                          {isActive && (
                            <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                              Processing...
                            </span>
                          )}
                          {isCompleted && (
                            <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                              ✓ Complete
                            </span>
                          )}
                        </div>
                        <span className={`text-sm font-medium ${
                          isActive ? 'text-blue-700' :
                          isCompleted ? 'text-green-700' :
                          'text-gray-500'
                        }`}>
                          {Math.round(progress)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${
                            isActive ? 'bg-blue-600' :
                            isCompleted ? 'bg-green-600' :
                            'bg-gray-400'
                          }`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {step === 'preview' && analysis && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Review Transactions</h3>
                <p className="text-gray-600">
                  Found {mappedTransactions.length} transactions. Select which ones to import.
                </p>
              </div>

              {/* Column detection info removed */}

              {/* Transaction List */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedTransactions.size === mappedTransactions.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTransactions(new Set(mappedTransactions.map((_, i) => i)));
                        } else {
                          setSelectedTransactions(new Set());
                        }
                      }}
                      className="rounded border-gray-300 text-blue-600 mr-3"
                    />
                    <span className="font-medium">Select All ({selectedTransactions.size} of {mappedTransactions.length})</span>
                  </label>
                </div>

                <div className="max-h-96 overflow-y-auto">
                  {mappedTransactions.map((transaction, index) => (
                    <div key={index} className={`border-b px-4 py-3 hover:bg-gray-50 ${
                      transaction.is_duplicate ? 'bg-yellow-50 border-yellow-200' : ''
                    }`}>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedTransactions.has(index)}
                          onChange={() => toggleTransactionSelection(index)}
                          className="rounded border-gray-300 text-blue-600 mr-3"
                        />
                        <div className="flex-1 flex justify-between items-center">
                          <div>
                            <div className={`font-medium ${
                              transaction.is_duplicate ? 'text-amber-800' : 'text-gray-900'
                            }`}>
                              {transaction.description}
                              {transaction.is_duplicate && (
                                <span className="ml-2 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                                  Duplicate
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600">
                              {formatDate(transaction.transaction_date)}
                              {transaction.ai_category && (
                                <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                  <span className="mr-1">{transaction.category_icon || '💰'}</span>
                                  {transaction.ai_category}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`font-medium ${
                              transaction.total_amount > 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {formatCurrency(transaction.total_amount)}
                            </div>
                            {!transaction.is_valid && (
                              <span className="text-xs text-red-600">Validation Error</span>
                            )}
                          </div>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Force Import Duplicates Option */}
              {mappedTransactions.some(tx => tx.is_duplicate) && (
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={forceImportDuplicates}
                      onChange={(e) => setForceImportDuplicates(e.target.checked)}
                      className="rounded border-gray-300 text-amber-600 mr-3"
                    />
                    <div>
                      <span className="font-medium text-amber-800">Force Import Duplicates</span>
                      <p className="text-sm text-amber-700 mt-1">
                        Import transactions marked as duplicates anyway. Use this when you know there are legitimate identical transactions (e.g., multiple subscription charges on the same day).
                      </p>
                    </div>
                  </label>
                </div>
              )}
            </div>
          )}

          {step === 'importing' && (
            <div className="text-center space-y-6">
              <Loader className="h-16 w-16 text-blue-600 mx-auto animate-spin" />
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Importing Transactions</h3>
                <p className="text-gray-600">
                  Adding {selectedTransactions.size} transactions to your account...
                </p>
              </div>
            </div>
          )}

          {step === 'complete' && (
            <div className="text-center space-y-6">
              <CheckCircle className="h-16 w-16 text-green-600 mx-auto" />
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Import Complete!</h3>
                <div className="text-gray-600 space-y-1">
                  {importResults && (
                    <>
                      <p>Successfully imported <span className="font-semibold text-green-600">{importResults.imported_count}</span> transactions to {account?.name}.</p>
                      {importResults.skipped_count > 0 && (
                        <p className="text-sm text-amber-600">
                          Skipped {importResults.skipped_count} duplicate transaction{importResults.skipped_count !== 1 ? 's' : ''}.
                        </p>
                      )}
                      <p className="text-sm text-gray-500 mt-2">
                        Total processed: {importResults.total_processed}
                      </p>
                    </>
                  )}
                  {!importResults && (
                    <p>Successfully imported {selectedTransactions.size} transactions to {account?.name}.</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50">
          {step === 'upload' && (
            <>
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={processFile}
                disabled={files.length === 0 || !account}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Parse {files.length === 1 ? 'File' : `${files.length} Files`}
              </button>
            </>
          )}

          {step === 'row-selection' && (
            <>
              <button
                type="button"
                onClick={() => setStep('upload')}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Back
              </button>
              <button
                type="button"
                onClick={processSelectedRows}
                disabled={selectedRows.size === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Analyze Selected Rows ({selectedRows.size})
              </button>
            </>
          )}

          {step === 'preview' && (
            <>
              <button
                type="button"
                onClick={() => setStep('upload')}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Back
              </button>
              <button
                type="button"
                onClick={importTransactions}
                disabled={selectedTransactions.size === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Import {selectedTransactions.size} Transactions
              </button>
            </>
          )}

          {step === 'complete' && (
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
            >
              Done
            </button>
          )}
        </div>
    </BaseModal>
  );
};

export default ImportTransactionsModal;